import { map } from 'modules/utils'

/**
 * Compare years between two date objects
 * @param {Object} firstDate
 * @param {Object} secondDate
 * @returns {boolean}
 */
const isSameYear = (firstDate, secondDate) =>
  isDate(firstDate) &&
  isDate(secondDate) &&
  firstDate.getFullYear() === secondDate.getFullYear()

/**
 * Compare months between two date objects
 * @param {Object} firstDate
 * @param {Object}secondDate
 * @returns {boolean}
 */
const isSameMonth = (firstDate, secondDate) =>
  isDate(firstDate) &&
  isDate(secondDate) &&
  firstDate.getMonth() === secondDate.getMonth()

/**
 * Test object is Date
 * @param {Object} inputDate
 * @returns {boolean}
 */
const isDate = (inputDate) => Object.prototype.toString.call(inputDate) === "[object Date]" && inputDate !== 'Invalid Date'

const monthNames = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
]

const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

/**
 * Get a full month's name (F)
 * @param {number} index
 * @returns {string}
 */
const getMonthByIndex = (index) => monthNames[index]

/**
 * Get a short month's name (M)
 * Example: 'Jan'
 * @param {number} index
 * @returns {string}
 */
const getShortMonthName = (index) => getMonthByIndex(index).substring(0, 3)

/**
 * Get month's index from a short name
 * Example input: Jan
 * @param name
 * @returns {number}
 */
const getMonthIndexFromShortName = (name) => {
  let matchingIndex = -1
  map((monthName, index) => {
    if (monthName.substr(0, 3) === name) {
      matchingIndex = index
    }
  }, monthNames)

  return matchingIndex
}

/**
 * Get next day
 * @param {Date} current
 * @returns {Date}
 */
const getNextDay = (current) => {
  return new Date(current.setDate(current.getDate() + 1))
}

/**
 * Get next month index
 * @param {Date}  current
 * @returns {Date}
 */
const getNextMonth = (current) => {
  return new Date(current.setMonth(current.getMonth() + 1))
}

/**
 * Get previous day
 * @param {Date} current
 * @returns {Date}
 */
const getPrevDay = (current) => {
  return new Date(current.setDate(current.getDate() - 1))
}

/**
 * Get previous month
 * @param {Date} current
 * @returns {Date}
 */
const getPrevMonth = (current) => {
  return new Date(current.setMonth(current.getMonth() - 1))
}

/**
 * Get the day after a week
 * @param {Date} current
 * @returns {Date}
 */
const nextDayAfterWeek = (current) => {
  return new Date(current.setDate(current.getDate() + 7))
}

/**
 * Get the day before a week
 * @param {Date} current
 * @returns {Date}
 */
const prevDayBeforeWeek = (current) => {
  return new Date(current.setDate(current.getDate() - 7))
}

/**
 *
 * @param {Date} currentDate
 * @returns {{}|{startDay: Date, endDay: Date}}
 */
const getMonthRange = (currentDate) => {
  if (!isDate(currentDate)) {
    return {}
  }

  return {
    startDay: new Date(currentDate.getFullYear(), currentDate.getMonth(), 1),
    endDay: new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0)
  }
}

/**
 *
 * @param {Date} currentDate
 * @returns {{}|{startDay: Date, endDay: Date}}
 */
const getWeekRange = (currentDate) => {
  if (!isDate(currentDate)) {
    return {}
  }

  return {
    startDay: new Date(currentDate.setDate(currentDate.getDate() - currentDate.getDay())),
    endDay: new Date(currentDate.setDate(currentDate.getDate() - currentDate.getDay() + 6)),
  }
}

/**
 * Convert YYYY-MMM-dd to native Date object
 * Example input: 2019-Dec-20
 * @param {Array} matches
 * @returns {Date}
 */
const getDateFromQuery = (matches) => {
  if (matches) {
    const year = matches[1]
    const shortMonthName = matches[2]
    const day = matches[3]
    const month = getMonthIndexFromShortName(shortMonthName)

    return new Date(year, month, day)
  }
}

/**
 *
 * @param {Date} inputDate
 * @param {string} formatType
 * @returns {string}
 */
const loadFormatDate = (inputDate, formatType) => {
  if (!isDate(inputDate) || isNaN(inputDate.valueOf()) || !formatType) {
    return ''
  }

  let result = ''

  const month = inputDate.getMonth() + 1
  const monthContext = month < 10 ? '0' + month : month

  const day = inputDate.getDate()
  const dayContext = day < 10 ? '0' + day : day

  const hour = inputDate.getHours()
  const hourContext = hour < 10 ? '0' + hour : hour

  const minute = inputDate.getMinutes()
  const minuteContext = minute < 10 ? '0' + minute : minute

  const second = inputDate.getSeconds()
  const secondContext = second < 10 ? '0' + second : second

  switch (formatType) {
    case 'F YYYY':
      result = getMonthByIndex(inputDate.getMonth()) + ' ' + inputDate.getFullYear()
      break;

    case 'YYYY':
      result = inputDate.getFullYear()
      break;

    // Example: December 15 2019
    case 'F dd YYYY':
      result = getMonthByIndex(inputDate.getMonth()) + ' ' + inputDate.getDate() + '' + inputDate.getFullYear()
    break;

    // Example: December 15
    case 'F dd':
      result = getMonthByIndex(inputDate.getMonth()) + ' ' + inputDate.getDate()
      break;

    // Example: January 15, 2019
    case 'F dd, YYYY':
      result = getMonthByIndex(inputDate.getMonth()) + ' ' + inputDate.getDate() + ', ' + inputDate.getFullYear()
      break;

    // Example: 15, 2019
    case 'dd, YYYY':
      result = inputDate.getDate() + ', ' + inputDate.getFullYear()
      break;

    // Example: yyyy - Dec - 17
    case 'YYYY - M - dd':
      let spectator = '-'

      result += inputDate.getFullYear()
      result += spectator
      result += getShortMonthName(inputDate.getMonth())
      result += spectator
      result += inputDate.getDate()

      break;

    case 'yyyy-mm+1-dd':
      // Adjust month+1 to match @fullcalendar lib
      result = inputDate.getFullYear() + '-' + (inputDate.getMonth() + 1) + '-' + inputDate.getDate()
      break;

    case 'yyyy-mm-dd':
      result = inputDate.getFullYear() + '-' + monthContext + '-' + dayContext
      break;

    case 'iso8601':
      result = `${inputDate.getFullYear()}-${monthContext}-${dayContext}T${hourContext}:${minuteContext}:${secondContext}`
      break;
    // mm dd YYYY
    // Example: 12 15 2019
    default:
      result = (inputDate.getMonth() + 1) + ' ' + inputDate.getDate() + ' ' + inputDate.getFullYear()
      break;
  }

  return result
}

export {
  monthNames,
  dayNames,
  isSameYear,
  isSameMonth,
  isDate,
  getDateFromQuery,
  getMonthByIndex,
  getNextDay,
  getNextMonth,
  getPrevDay,
  getPrevMonth,
  getMonthRange,
  getWeekRange,
  nextDayAfterWeek,
  prevDayBeforeWeek,
  loadFormatDate
}
